import type { CollisionMap } from "./CollisionMap";

interface Node {
  x: number;
  y: number;
  g: number;
  h: number;
  f: number;
  parent: Node | null;
}

/** 8-directional neighbours: cardinal + diagonal. */
const DIRS: Array<[number, number, number]> = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, Math.SQRT2],
  [1, -1, Math.SQRT2],
  [-1, 1, Math.SQRT2],
  [-1, -1, Math.SQRT2],
];

/** Maximum nodes to expand before giving up (prevents frame stalls). */
const MAX_ITERATIONS = 512;

/**
 * A* pathfinder over the tile-based CollisionMap.
 * Returns an array of integer tile centres (x+0.5, y+0.5) from start to goal,
 * or null if no path exists.
 */
export function findPath(
  collision: CollisionMap,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
): Array<{ x: number; y: number }> | null {
  const sx = Math.floor(fromX);
  const sy = Math.floor(fromY);
  const gx = Math.floor(toX);
  const gy = Math.floor(toY);

  // Goal is blocked — no valid path
  if (collision.isBlockedAt(gx, gy)) return null;

  // Already on the goal tile
  if (sx === gx && sy === gy) return [{ x: toX, y: toY }];

  const key = (x: number, y: number) => (y << 8) | x;

  const open: Node[] = [];
  const closed = new Set<number>();

  const h = (x: number, y: number) => {
    // Chebyshev distance (matches 8-dir movement cost)
    const dx = Math.abs(x - gx);
    const dy = Math.abs(y - gy);
    return Math.max(dx, dy) + (Math.SQRT2 - 1) * Math.min(dx, dy);
  };

  const start: Node = { x: sx, y: sy, g: 0, h: h(sx, sy), f: h(sx, sy), parent: null };
  open.push(start);

  let iterations = 0;

  while (open.length > 0 && iterations < MAX_ITERATIONS) {
    iterations++;

    // Find node with lowest f (simple linear scan — grid is small)
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i]!.f < open[bestIdx]!.f) bestIdx = i;
    }
    const current = open[bestIdx]!;
    open.splice(bestIdx, 1);

    if (current.x === gx && current.y === gy) {
      return reconstructPath(current, toX, toY);
    }

    closed.add(key(current.x, current.y));

    for (const [ddx, ddy, cost] of DIRS) {
      const nx = current.x + ddx;
      const ny = current.y + ddy;

      if (closed.has(key(nx, ny))) continue;
      if (collision.isBlockedAt(nx, ny)) continue;

      // Check directional edge flags
      if (collision.isStepBlocked(current.x, current.y, nx, ny)) continue;

      // Prevent diagonal corner-cutting through blocked tiles
      if (ddx !== 0 && ddy !== 0) {
        if (collision.isBlockedAt(current.x + ddx, current.y) ||
            collision.isBlockedAt(current.x, current.y + ddy)) {
          continue;
        }
      }

      const ng = current.g + cost;

      const existing = open.find((n) => n.x === nx && n.y === ny);
      if (existing) {
        if (ng < existing.g) {
          existing.g = ng;
          existing.f = ng + existing.h;
          existing.parent = current;
        }
        continue;
      }

      const hn = h(nx, ny);
      open.push({ x: nx, y: ny, g: ng, h: hn, f: ng + hn, parent: current });
    }
  }

  // No path found (or iteration limit reached)
  return null;
}

function reconstructPath(
  goal: Node,
  exactGoalX: number,
  exactGoalY: number,
): Array<{ x: number; y: number }> {
  const tiles: Node[] = [];
  let n: Node | null = goal;
  while (n) {
    tiles.push(n);
    n = n.parent;
  }
  tiles.reverse();

  // Convert to tile-centre waypoints, final point uses exact click position
  const path: Array<{ x: number; y: number }> = [];
  for (let i = 1; i < tiles.length - 1; i++) {
    path.push({ x: tiles[i]!.x + 0.5, y: tiles[i]!.y + 0.5 });
  }
  // Last waypoint is the exact click position (within the goal tile)
  path.push({ x: exactGoalX, y: exactGoalY });

  return path;
}
